/**
 * Blue Speed Construction — approved website copy.
 *
 * This file is the single source of truth for the site's wording. It mirrors the
 * client's content document section for section, and the sentences are kept
 * verbatim so nothing is lost or paraphrased in a template.
 *
 * To change wording on the site, change it here.
 */

/* ---------------------------------------------------------------
   1. Company Overview
--------------------------------------------------------------- */

export const overview = {
  established: "1996",

  intro:
    "Established since 1996, Blue Speed Construction Pte. Ltd. is a Singapore-based building maintenance and construction company.",

  scope:
    "We provide a wide range of services, including general building maintenance, renovation and reinstatement works, project management support, building and civil works, as well as specialist rope access solutions for high-rise and difficult-to-reach areas.",

  experience:
    "Over the years, we have built our business through hands-on experience, reliable workmanship and close coordination with our clients.",

  approach:
    "We understand that every building and project has its own challenges, so we focus on providing practical solutions that are safe and suitable for the actual site conditions.",
};

export const bcaRegistrations = [
  { code: "CW01", title: "General Building" },
  { code: "CR09", title: "Repairs and Redecoration" },
  {
    code: "FM02",
    title: "Housekeeping, Cleansing, Desilting and Conservancy Services",
  },
];

/* ---------------------------------------------------------------
   2. Our Journey
--------------------------------------------------------------- */

export const journey = [
  {
    stage: "The beginning",
    heading: "One project at a time",
    body: "Blue Speed started with a small team and limited resources, taking on subcontract works and building its experience one project at a time.",
  },
  {
    stage: "The early years",
    heading: "Management on site, every day",
    body: "In the early years, our management team remained closely involved in daily site operations, client coordination and work execution. This hands-on approach helped us understand what clients value most: clear communication, dependable support and work completed properly.",
  },
  {
    stage: "Growth",
    heading: "From subcontractor to main contractor",
    body: "As our experience and capabilities grew, we gradually expanded to taking on larger and more complete project scopes as a main contractor.",
  },
  {
    stage: "What drives us",
    heading: "Experience, improvement and trust",
    body: "Our growth has been driven by practical site experience, continuous improvement and the trust built with our clients over the years.",
  },
  {
    stage: "Moving forward",
    heading: "Strengthening our presence in Singapore",
    body: "Moving forward, we remain focused on strengthening our presence in Singapore, developing our workforce and building long-term relationships with clients through consistent service and reliable project delivery.",
  },
];

/* ---------------------------------------------------------------
   3. Services
--------------------------------------------------------------- */

export const services = {
  intro:
    "Blue Speed provides a broad range of building maintenance, construction support and rope access services.",

  core: [
    "General building maintenance",
    "Renovation and reinstatement works",
    "Internal and external façade cleaning",
    "Internal and external painting works",
  ],

  other: [
    "Project management support",
    "Building and civil works",
    "Installation and dismantling works",
    "Building minor repairs",
    "Window and skylight cleaning",
    "Sealant installation and replacement",
    "Cladding and glazing panel replacement",
    "Periodic façade inspection works - PFI",
    "Signage works",
  ],
};

/* ---------------------------------------------------------------
   4. Business Divisions
--------------------------------------------------------------- */

export const buildingCivilDivision = {
  number: "4.1",
  name: "Building and Civil Division",
  paragraphs: [
    "Our Building and Civil Division carries out construction, civil, repair and general building maintenance works.",
    "The division also handles renovation and reinstatement projects, providing practical and cost-effective solutions for day-to-day building operations, upgrading works and long-term property maintenance.",
    "We work closely with building owners, managing agents, consultants and site teams to coordinate the work, manage site conditions and minimise disruption to building occupants.",
  ],
  focus:
    "Our focus is simple: understand the problem, recommend a workable solution and complete the work to a consistent standard.",
};

export const ropeAccessDivision = {
  number: "4.2",
  name: "Rope Access Services Division",

  intro:
    "Our Rope Access Services Division specializes in work at height and areas where conventional access methods may be difficult, costly or disruptive.",

  techniquesLead:
    "Using industrial rope access techniques, our trained technicians are able to access high-rise façades, external structures and other difficult locations to carry out:",

  works: [
    "Cleaning works",
    "Inspection works",
    "Painting works",
    "Sealant and minor repair works",
    "Signage installation and maintenance",
    "Selected replacement works",
    "Façade maintenance",
  ],

  efficiency:
    "Rope access allows us to work efficiently in areas where scaffolding, gondolas or mobile access equipment may not be practical.",

  obstruction:
    "It also helps reduce obstruction to building entrances, pedestrian areas and surrounding operations, making it suitable for occupied commercial buildings and busy environments.",
};

/* 4.2.1 Rope Access Capability */

export const ropeAccessCapability = {
  number: "4.2.1",
  name: "Rope Access Capability",

  intro:
    "Rope access is a flexible and efficient alternative to conventional access methods such as scaffolding and staging, particularly for high-rise buildings and locations with limited access.",

  advantagesLead:
    "Depending on the site and work scope, rope access can provide several advantages:",

  advantages: [
    "Less disruption to building occupants",
    "Reduced obstruction at ground level",
    "Improved efficiency for inspection, cleaning and repair works",
  ],

  practice:
    "Our rope access works are carried out by trained personnel using internationally recognized industrial rope access practices.",

  systems:
    "Technicians work with independent working and backup rope systems, supported by proper supervision, risk assessments, safe work procedures and rescue planning.",

  temporary:
    "As the rope systems are temporary, they can be removed after each work shift, reducing long-term obstruction and security concerns.",
};

/* ---------------------------------------------------------------
   5. Quality, Safety and Training
--------------------------------------------------------------- */

export const qualitySafety = {
  belief:
    "At Blue Speed, we believe that good service is not only about completing the work. It is also about communicating clearly, responding quickly and taking responsibility throughout the project.",

  pillars: [
    {
      key: "quality",
      title: "Quality of work",
      body: "We aim to carry out every job properly, from the initial site inspection and planning stage to execution, reporting and final handover.",
    },
    {
      key: "safety",
      title: "Safety",
      body: "Safety is a key part of how we work. Our teams follow site-specific risk assessments, safe work procedures, permit-to-work requirements and applicable safety regulations.",
    },
    {
      key: "training",
      title: "Training and workforce development",
      body: "We also place strong emphasis on training and workforce development. Our workers attend relevant training courses, safety briefings and technical workshops to improve their skills and stay updated with current work practices and technologies.",
    },
  ],

  productivity:
    "We continue to explore practical ways to improve productivity and solve site challenges, including the use of rope access systems, suitable equipment and more efficient work methods.",

  goal:
    "Our goal is to provide clients with dependable support, practical solutions and work that is completed safely and professionally.",
};
